// Decides *when* an expensive derived view (the tree, the asset-path table)
// gets rebuilt. Knows nothing about Vue or about what it is rebuilding.
//
// Two rules, both driven by measurement rather than a fixed constant:
//
//   1. Nobody looking, nothing rebuilt. A rebuild whose result is never
//      rendered is pure cost. The dirty flag is kept, so the moment someone
//      observes the selection it catches up.
//
//   2. Rebuild at most once per interval, and derive that interval from what
//      the last rebuild actually cost. A fixed interval does not survive
//      graph growth: the tree takes ~5s at 25,400 individuals and the 300ms
//      debounce it used to sit behind meant it re-triggered itself
//      continuously — 82 rebuilds in 633s on one measured sync, ~44% of the
//      wall clock, every one of them discarded by the next. Scaling the
//      interval with the cost keeps rebuilds a bounded fraction of wall clock
//      however large the graph gets.
//
// The interval is generous during a sync and tight when idle, but it always
// applies. An idle change still rebuilds on the next tick, because nothing
// ran recently and the elapsed time already exceeds the interval; what the
// idle interval catches is a burst arriving faster than rebuilds complete.
//
// Every run is scheduled rather than synchronous, including the first one
// after observe(). At 25k a tree rebuild blocks for ~5s; running it inline
// from a component's mounted() hook would prevent that component painting a
// loading state first.

// While a sync is running. The multiplier bounds rebuilds to 1/k of wall
// clock, since the interval is measured from the start of the previous run.
const DEFAULT_SYNC_MIN_INTERVAL_MS = 3000
const DEFAULT_SYNC_COST_MULTIPLIER = 6

// Idle. A single change still rebuilds on the next tick — nothing ran
// recently, so the elapsed time already exceeds the interval. What this
// bounds is a *burst*: the second and later events land inside the window
// and coalesce into one rebuild instead of blocking the main thread once
// each. Measured from the end of the previous run, a multiplier of 1 leaves
// a gap as long as the rebuild itself.
const DEFAULT_IDLE_MIN_INTERVAL_MS = 250
const DEFAULT_IDLE_COST_MULTIPLIER = 1

// Run `fn` once the browser has actually painted. A rAF callback still runs
// before the paint, so the timeout inside it is what lands after. Falls back
// to a plain timeout where rAF does not exist (tests, workers).
function afterNextPaint(fn) {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => setTimeout(fn, 0))
  } else {
    setTimeout(fn, 0)
  }
}

class RebuildScheduler {
  /**
   * @param {Object} opts
   * @param {() => void} opts.run - performs the rebuild, synchronously
   * @param {() => boolean} opts.isSyncing - true while a bulk sync is running
   * @param {() => number} opts.lastDurationMs - cost of the previous rebuild
   * @param {number} [opts.syncMinIntervalMs] - floor while syncing
   * @param {number} [opts.syncCostMultiplier] - interval = cost x this
   * @param {number} [opts.idleMinIntervalMs] - floor when not syncing
   * @param {number} [opts.idleCostMultiplier] - interval = cost x this
   * @param {(running: boolean) => void} [opts.onStateChange] - called around
   *   each run so a view can show that work is under way
   */
  constructor({
    run,
    isSyncing,
    lastDurationMs,
    syncMinIntervalMs = DEFAULT_SYNC_MIN_INTERVAL_MS,
    syncCostMultiplier = DEFAULT_SYNC_COST_MULTIPLIER,
    idleMinIntervalMs = DEFAULT_IDLE_MIN_INTERVAL_MS,
    idleCostMultiplier = DEFAULT_IDLE_COST_MULTIPLIER,
    onStateChange = null,
  }) {
    this._run = run
    this._isSyncing = isSyncing
    this._lastDurationMs = lastDurationMs
    this._syncMinIntervalMs = syncMinIntervalMs
    this._syncCostMultiplier = syncCostMultiplier
    this._idleMinIntervalMs = idleMinIntervalMs
    this._idleCostMultiplier = idleCostMultiplier
    this._onStateChange = onStateChange

    this._running = false
    this._flushPending = false
    this._observers = 0
    this._dirty = false
    this._timer = null
    this._lastRunAt = 0
  }

  get isObserved() {
    return this._observers > 0
  }

  /** True from the moment a run is announced until it finishes. */
  get isRunning() {
    return this._running
  }

  /**
   * Register interest. Returns an unsubscribe function — callers should use
   * that rather than pairing a manual unobserve(), since a missed call leaks
   * an observer and the selection then rebuilds forever.
   */
  observe() {
    this._observers++
    if (this._observers === 1 && this._dirty) this._schedule()
    let released = false
    return () => {
      if (released) return
      released = true
      this._release()
    }
  }

  _release() {
    this._observers = Math.max(0, this._observers - 1)
    if (this._observers === 0) this._cancel()
  }

  /**
   * Something changed. Rebuilds only if someone is observing; otherwise the
   * dirty flag is remembered and observe() picks it up.
   */
  request() {
    this._dirty = true
    if (!this.isObserved) return
    this._schedule()
  }

  /** Run now regardless of observers — for explicit user actions. */
  flush() {
    this._cancel()
    // A run is already pending or executing. Rather than queue a second one
    // behind it, have it follow up immediately once it finishes.
    if (this._running) {
      this._flushPending = true
      return
    }
    this._fire()
  }

  _schedule() {
    // Already queued, or a run is pending or executing. Either way the latest
    // state is picked up: a queued timer reads it when it fires, and a run in
    // flight re-checks _dirty when it finishes (see _fire).
    //
    // Checking only _timer let requests arriving while a run waited for its
    // paint queue runs of their own: _fire clears the timer immediately, and
    // with _lastRunAt still pointing at the previous run the computed delay
    // was zero. On the Fabege graph that showed as bursts of five skipped
    // runs in the same millisecond, and a rebuild cadence of ~2.5s instead of
    // the >=3.3s the policy asks for.
    if (this._timer || this._running) return
    this._timer = setTimeout(() => this._fire(), this._delayMs())
  }

  _delayMs() {
    // Both cases are the same rule with different constants. The idle path
    // is not exempt: `isSyncing` goes false while the last batches of a sync
    // are still being dispatched, and treating that as "rebuild instantly"
    // meant every remaining batch blocked the main thread for a full rebuild.
    const syncing = this._isSyncing()
    const interval = Math.max(
      syncing ? this._syncMinIntervalMs : this._idleMinIntervalMs,
      (syncing ? this._syncCostMultiplier : this._idleCostMultiplier) *
        this._lastDurationMs()
    )
    return Math.max(0, interval - (Date.now() - this._lastRunAt))
  }

  _cancel() {
    if (!this._timer) return
    clearTimeout(this._timer)
    this._timer = null
  }

  _fire() {
    this._timer = null
    this._dirty = false

    // Announce, let the browser paint, and only then block on the run. The
    // run is synchronous and takes seconds at production graph sizes, so a
    // flag raised inside it can never reach the screen — this is the only
    // frame the browser gets, and without it the UI simply freezes with no
    // indication that anything is happening.
    this._setRunning(true)
    afterNextPaint(() => {
      try {
        this._run()
      } finally {
        // Stamped when the run *finishes*, not when it starts. Stamping at the
        // start meant an interval derived from the cost elapsed exactly as the
        // run ended, so the next one began with no gap at all — three
        // back-to-back 11s rebuilds were observed on the Fabege graph.
        this._lastRunAt = Date.now()
        this._setRunning(false)

        // Requests that arrived while this run was pending or executing were
        // held back rather than queued as runs of their own (see _schedule).
        // Pick them up now, paced from a _lastRunAt that is actually current.
        if (this._flushPending) {
          this._flushPending = false
          this._fire()
        } else if (this._dirty && this.isObserved) {
          this._schedule()
        }
      }
    })
  }

  _setRunning(running) {
    if (this._running === running) return
    this._running = running
    if (this._onStateChange) this._onStateChange(running)
  }
}

export { RebuildScheduler }
